<template>
  <main class="px-4 pt-4">
    <div class="d-flex flex-row justify-content-between align-items-center mb-4">
      <h1>
        {{ $t('Shipping orders') }}
      </h1>
      <dx-button
        :text="$t('New shipping order')"
        icon="ace-icon icon-plus"
        type="success"
        styling-mode="contained"
        class="custom-btn"
        @click="$router.push('/add-freight-order')"
      />
    </div>

    <dx-data-grid
      ref="freightOrdersGrid"
      class="mb-5"
      :data-source="dataSource"
      :remote-operations="remoteOperations"
      :allow-column-reordering="true"
      :show-borders="true"
      :column-min-width="70"
      :column-auto-width="true"
      :allow-column-resizing="true"
      column-resizing-mode="widget"
      :hover-state-enabled="true"
      :on-row-dbl-click="onRowDblClick"
      :on-editor-preparing="onEditorPreparing"
      @toolbar-preparing="onToolbarPreparing"
      @cell-prepared="onCellPrepared"
    >
      <dx-column
        data-field="id"
        caption="ID"
        :fixed="true"
        fixed-position="left"
        :allow-editing="false"
        :allow-header-filtering="true"
        :allow-search="true"
      />

      <!--
      <dx-column
        data-field="company"
        :caption="$t('company')"
        :fixed="true"
        fixed-position="left"
        :allow-editing="user.can_select_company"
        :lookup="companyLookup"
        :allow-search="true"
      />-->

      <dx-column
        data-field="company_name"
        :caption="$t('Company')"
        :allow-editing="false"
      />
      <dx-column
        data-field="status"
        :caption="$t('Status')"
        :lookup="statusLookup"
        :allow-search="false"
        :width="240"
      />
      <dx-column
        v-if="user.is_manager"
        data-field="paid_date"
        :caption="$t('Payment date')"
        :allow-search="false"
        :allow-editing="false"
        data-type="date"
      />
      <dx-column
        v-if="user.is_manager"
        data-field="totals.total_cost"
        :caption="$t('Approved cost')"
        :allow-search="false"
        data-type="number"
      />
      <dx-column
        v-if="user.is_manager"
        data-field="totals.total_expenses"
        :caption="$t('Total expenses')"
        :allow-search="false"
        data-type="number"
      />
      <dx-column
        v-if="user.is_manager"
        data-field="totals.total_profit"
        :caption="$t('Total profit')"
        :allow-search="false"
        data-type="number"
      />

      <dx-column
        data-field="status_comment"
        :caption="$t('Status comment')"
        :allow-search="false"
        :width="240"
      >
        <dx-custom-rule
          :validation-callback="validateStatus"
          :reevaluate="true"
          :message="$t('The field is required')"
        />
      </dx-column>
      <dx-column
        data-field="customer_order_number"
        :caption="$t('Customer shipping order number')"
        data-type="string"
      />
      <dx-column
        data-field="railway_transfer_subcode"
        :caption="$t('Railway transfer subcode')"
        data-type="string"
      />
      <dx-column
        data-field="consignor"
        :caption="$t('Consignor')"
      />
      <dx-column
        data-field="consignee"
        :caption="$t('Consignee')"
      />
      <dx-column
        data-field="delivery_terms"
        :caption="$t('Delivery terms')"
      />
      <dx-column
        data-field="freight_description"
        :caption="$t('Freight description')"
      />
      <dx-column
        data-field="dispatch_location"
        :caption="$t('Dispatch location')"
      />
      <dx-column
        data-field="border_crossing_point"
        :caption="$t('Border crossing point')"
        :lookup="borderCrossingPointLookup"
      />
      <dx-column
        data-field="destination"
        :caption="$t('Destination')"
      />
      <dx-column
        data-field="order_contacts_phone"
        :caption="$t('Phone')"
      />
      <dx-column
        data-field="order_contacts_email"
        :caption="$t('Email')"
      />
      <dx-column
        data-field="order_contacts"
        :caption="$t('Order contacts')"
      />
      <dx-column
        data-field="notes"
        :caption="$t('Notes')"
      />
      <dx-column
        data-field="freightordercontainer"
        :caption="$t('Container')"
        :allow-header-filtering="false"
        :allow-editing="false"
      />
      <dx-column
        data-field="container_count"
        :caption="$t('Container count')"
        :allow-search="false"
      />
      <dx-column
        data-field="create_date"
        :caption="$t('Freight order date')"
        data-type="date"
        :allow-search="false"
        :allow-editing="false"
      />
      <dx-column
        data-field="author"
        :caption="$t('Author')"
        :allow-search="false"
        :allow-editing="false"
        cell-template="author"
      />
      <dx-column
        data-field="updated_by"
        :caption="$t('Updated by')"
        :allow-search="false"
        :allow-editing="false"
        cell-template="updated_by"
      />
      <dx-column
        :buttons="editButtons"
        type="buttons"
        :fixed="true"
        :allow-resizing="false"
      />
      <template #author="{ data }">
        {{ data.data.author ? `${data.data.author.first_name} ${data.data.author.last_name}` : '' }}
      </template>
      <template #updated_by="{data}">
        {{ data.data.updated_by ? `${data.data.updated_by.first_name}
        ${data.data.updated_by.last_name}` : '' }}
      </template>

      <dx-toolbar>
        <dx-item
          location="before"
          name="searchPanel"
        />
        <dx-item
          name="columnChooserButton"
        />
      </dx-toolbar>

      <dx-editing
        :allow-updating="true"
        :allow-deleting="true"
        :allow-adding="isMobile"
        :use-icons="true"
        mode="row"
      />
      <dx-header-filter :visible="true" />
      <dx-search-panel
        :visible="true"
        :highlight-case-sensitive="false"
      />
      <dx-export
        :enabled="true"
        :allow-export-selected-data="true"
        file-name="freight-orders"
      />
      <dx-column-chooser :enabled="true" />
      <dx-selection
        show-check-boxes-mode="always"
        mode="single"
      />
      <dx-state-storing
        :enabled="true"
        type="localStorage"
        :storage-key="`u${user.id}-freight-orders-grid-storage`"
      />
      <dx-paging :page-size="10" />
      <dx-pager
        :show-page-size-selector="true"
        :allowed-page-sizes="[10, 15, 20, 50]"
      />
    </dx-data-grid>
  </main>
</template>

<script>
import CustomStore from 'devextreme/data/custom_store';

import {
  DxDataGrid,
  DxExport,
  DxColumn,
  DxColumnChooser,
  DxEditing,
  DxHeaderFilter,
  // DxGroupPanel,
  DxSearchPanel,
  DxSelection,
  DxStateStoring,
  DxPaging,
  DxPager,
  DxToolbar,
  DxItem,
} from 'devextreme-vue/data-grid';

import { DxCustomRule } from 'devextreme-vue/validator';

import { DxButton } from 'devextreme-vue';

import { mapState } from 'vuex';
import ruMessages from 'devextreme/localization/messages/ru.json';
import { locale as localize, loadMessages } from 'devextreme/localization';

import mixinDetictingMobile from '@/mixins/mixin-detecting-mobile';

import createCustomStore from '@/utils/create-custom-store';
import createStatusLookup from '@/utils/create-status-lookup';

export default {
  name: 'FreightOrderList',
  components: {
    DxDataGrid,
    DxColumn,
    // DxGroupPanel,
    DxSearchPanel,
    DxEditing,
    DxHeaderFilter,
    DxExport,
    DxColumnChooser,
    DxSelection,
    DxStateStoring,
    DxPaging,
    DxPager,
    DxButton,
    DxCustomRule,
    DxToolbar,
    DxItem,
  },
  mixins: [mixinDetictingMobile],
  data: vue => ({
    containerStatusApproved: 2,
    remoteOperations: {
      sorting: true,
      filtering: true,
      paging: true,
    },
    dataSource: createCustomStore('tracking/api/freight_orders/'),
    statusLookup: createStatusLookup(vue),
    statusList: [],
  }),
  computed: {
    ...mapState(['user']),
    companyLookup: () => ({
      dataSource: {
        store: createCustomStore('profile/api/companies/'),
        key: 'id',
      },
      valueExpr: 'id',
      displayExpr(company) {
        return company ? `${company.name} ${company.inn || ''}` : '';
      },
      allowClearing: true,
    }),
    borderCrossingPointLookup: vue => ({
      dataSource: createCustomStore('tracking/api/locations/'),
      valueExpr: 'id',
      displayExpr(item) {
        return item ? item.location || vue.$t('not set') : '';
      },
    }),
    editButtons() {
      return [
        'edit',
        {
          hint: this.$t('Copy'),
          icon: 'export',
          onClick: async e => {
            this.$router.push({
              name: 'AddFreightOrder',
              query: {
                copiedId: e.row.data.id,
              },
            });
          },
        },
      ];
    },
  },
  async created() {
    loadMessages(ruMessages);
    localize(this.$i18n.locale);
    this.getStatusList();
  },
  methods: {
    onToolbarPreparing(e) {
      e.toolbarOptions.items.unshift({
        locateInMenu: 'auto',
        location: 'after',
        widget: 'dxButton',
        showText: 'inMenu',
        options: {
          icon: 'ace-icon icon-refresh',
          onClick: this.refreshDataGrid.bind(this),
          text: this.$t('refresh'),
          hint: this.$t('refresh'),
          type: 'normal',
          mode: 'text',
        },
      }, {
        locateInMenu: 'auto',
        location: 'after',
        widget: 'dxButton',
        showText: 'inMenu',
        options: {
          icon: 'ace-icon icon-close',
          onClick: this.refreshDataGrid.bind(this),
          text: this.$t('clear_filters'),
          hint: this.$t('clear_filters'),
          type: 'normal',
          mode: 'text',
        },
      }, {
        locateInMenu: 'auto',
        location: 'center',
        widget: 'dxButton',
        options: {
          icon: 'ace-icon icon-download',
          onClick: this.exportDocs,
          text: this.$t('Export documents for a period'),
          type: 'success',
          mode: 'text',
          elementAttr: { class: 'custom-btn' },
        },
      });
    },
    exportDocs() {
      this.$router.push('/export-tasks');
    },
    onCellPrepared(e) {
      if (this.user.is_manager) {
        return;
      }
      if (e.rowType === 'data' && e.column.command === 'edit' && e.row.data.status === this.containerStatusApproved) {
        const editLink = e.cellElement.querySelector('.dx-link-edit');
        const deleteLink = e.cellElement.querySelector('.dx-link-delete');
        if (editLink) {
          editLink.remove();
          deleteLink.remove();
        }
      }
    },
    onRowDblClick(e) {
      this.$router.push(`/freight-orders/${e.data.id}`);
    },
    refreshDataGrid() {
      this.$refs.freightOrdersGrid.instance.refresh();
    },
    clearFilter() {
      this.$refs.freightOrdersGrid.instance.clearFilter();
    },
    onEditorPreparing(e) {
      const self = this;
      if (e.parentType === 'dataRow' && e.dataField === 'status') {
        e.editorOptions.dataSource = new CustomStore({
          store: {
            type: 'array',
            data: createStatusLookup(self),
            key: 'id',
          },
          load: async () => {
            const { results, count } = await self.$http.get(`tracking/api/freight_orders/${e.row.key}/wf/`);
            return {
              data: results,
              totalCount: count,
            };
          },
          byKey(key) {
            return self.$http.get(`tracking/api/freight-order-statuses/${key}/`);
          },
        });
      }
      if (e.parentType === 'searchPanel') {
        e.editorOptions.stylingMode = 'outlined';
      }
      if (e.parentType === 'columnChooserButton') {
        e.editorOptions.icon = 'ace-icon icon-columns';
      }
    },
    async getStatusList() {
      const { results } = await this.$http.get('tracking/api/freight-order-statuses/');
      this.statusList = results;
    },
    validateStatus(e) {
      return !(this.statusList.find(it => it.id === e.data.status).comment_required && !e.data.status_comment);
    },
  },
};
</script>
